import { normalizeResolvedPath } from '../../../paths/normalize-resolved-path.js';
import { withCodeNavCacheSqlite } from '../../../sqlite/code-nav-cache/database.js';
import { listDirectImportTargetsForFileAsync } from '../query/list-direct-import-targets.js';
import { symbolIndexFileIsReadyAsync } from '../lifecycle/symbol-index-file-ready.js';
import { upsertSymbolIndexParentPath } from '../sqlite/symbol-index-sqlite.js';
import { indexOneSymbolFileForBuild } from './index-one-symbol-file-for-build.js';
import { normalizeSymbolIndexRelPath } from './normalize-symbol-index-rel-path.js';

export { indexOneSymbolFileForBuild } from './index-one-symbol-file-for-build.js';

const MAX_IMPORT_PREFETCH_DEPTH = 1;
const MAX_IMPORT_PREFETCH_COUNT = 8;

/**
 * Index a single file (and a shallow import closure) on demand.
 * Returns true when the requested file is ready for queries.
 */
export async function ensureSymbolIndexFileImpl(
  parentPath: string,
  filePath: string,
  opts?: { importDepth?: number },
): Promise<boolean> {
  const resolved = normalizeResolvedPath(parentPath);
  const relPath = normalizeSymbolIndexRelPath(filePath);
  if (!relPath) return false;

  const importDepth = opts?.importDepth ?? 0;

  await withCodeNavCacheSqlite((db) => {
    upsertSymbolIndexParentPath(db, resolved);
  });

  if (!(await symbolIndexFileIsReadyAsync(resolved, relPath))) {
    await indexOneSymbolFileForBuild(resolved, relPath, undefined);
  }

  if (importDepth > 0 && (await symbolIndexFileIsReadyAsync(resolved, relPath))) {
    const targets = (await listDirectImportTargetsForFileAsync(resolved, relPath)).slice(
      0,
      MAX_IMPORT_PREFETCH_COUNT,
    );
    for (const target of targets) {
      if (!(await symbolIndexFileIsReadyAsync(resolved, target))) {
        await ensureSymbolIndexFileImpl(resolved, target, { importDepth: importDepth - 1 });
      }
    }
  }

  return await symbolIndexFileIsReadyAsync(resolved, relPath);
}
